/**
 * The document workbench. This is where the officer lives.
 *
 * The page is built around one property: **it is never locked.** Generation
 * returns as soon as the deterministic half is written — statutory text, office
 * standing paragraphs, every substituted slot — so the document is on screen in
 * the first second and readable, selectable and scrollable while the case
 * paragraphs are still arriving. A `[C]` block that has not arrived says so in
 * the place it will stand, and the seconds are counted once, on the strip above
 * the document; a block that never arrives becomes an ordinary amber blank,
 * which is what it now is.
 *
 * Everything else here is a rule wearing a component. The diff card is rule 2.
 * The blank chips are rule 1. The greyed download with its failing rule beside it
 * is rule 3. The English pane's missing affordances are rule 5.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiFailure, api } from '../api/client'
import type {
  BlockView,
  DraftingNeedsView,
  GroundsView,
  MarkView,
  RepliesView,
  RewriteProposalView,
  UnresolvedSlotView,
} from '../api/client'
import { ErrorEnvelope } from '../components/bits'
import { isAnsweredParawise, isPleading } from '../components/pleading'
import { Replies, RepliesPanel } from '../components/replies'
import { D18Workspace } from '../components/d18-workspace'
import { Grounds, GroundsPanel, isPetition } from '../components/grounds'
import { UnsourcedCount } from '../components/workbench'
import {
  AtSelection,
  BlankCounter,
  MarkCounter,
  MarkList,
  MarkPanel,
  DiffCard,
  DraftingNeedsPanel,
  DraftView,
  EditBlockDialog,
  EnglishPane,
  FillDialog,
  InstructBar,
  IssuePanel,
  PreIssuePanel,
  StatutoryRefusal,
  TemplateDrawer,
  VersionList,
} from '../components/workbench'
import { plainWords, readOccurrence } from '../lib/blocks'
import { POLL_INTERVAL_MS, readAssembly } from '../lib/drafting'
import { readDate } from '../lib/period'
import { isDraftNow, useRefreshCase } from './case/acting'
import { ParticularsChangedNote } from './case/ParticularsChangedNote' // Phase A, Lane C

/** What a draft is made as: the purpose of a noting, and which instrument. */
interface Choice {
  purpose: string | null
  part: string | null
  /** Started on arrival from the case card, not by a button pressed here. */
  atOnce?: boolean
}

/**
 * A refusal goes where the drag was, when there was one.
 *
 * Every refusal path now carries the range the officer selected, so the answer
 * appears beside the question rather than at the foot of the document — but the
 * type still admits `null` and the fallback is the old, honest in-flow card. A
 * refusal is never dropped for want of somewhere to put it.
 */
function Beside({ range, children }: { range: Range | null; children: React.ReactNode }) {
  if (!range) return <>{children}</>
  return <AtSelection range={range}>{children}</AtSelection>
}

interface Selection {
  block: BlockView
  start: number
  end: number
  text: string
  /** The live range, re-measured as the officer scrolls. */
  range: Range
}

export function Workbench() {
  const { caseId, documentType } = useParams()
  const [params, setParams] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const client = useQueryClient()
  const refreshCase = useRefreshCase(caseId ?? '')

  const versionId = params.get('v')
  const [english, setEnglish] = useState(false)
  const [filling, setFilling] = useState<UnresolvedSlotView | null>(null)
  // ADR-025. The mark the officer has clicked, and is being shown the reason
  // for. Held here rather than in the draft because the panel sits outside it.
  const [openMark, setOpenMark] = useState<MarkView | null>(null)
  // The list the *N things to check* counter opens.
  const [listingMarks, setListingMarks] = useState(false)
  const [selection, setSelection] = useState<Selection | null>(null)
  const [proposal, setProposal] = useState<RewriteProposalView | null>(null)
  const [lastInstruction, setLastInstruction] = useState('')
  const [editing, setEditing] = useState<BlockView | null>(null)
  const [showTemplate, setShowTemplate] = useState(false)
  /** Which instrument to render, where a template's parts are alternatives. */
  const [part, setPart] = useState<string | null>(null)
  /**
   * What this noting is for. The server suggests one from the case's record;
   * the officer confirms or changes it here, in one click. The choice selects
   * the closing formula **verbatim** from the skeleton's own table — a model
   * never writes a closing line — and tells the drafter what the note argues
   * for. Null until the officer or the suggestion settles it.
   */
  const [purpose, setPurpose] = useState<string | null>(null)
  // The officer's per-document language (entry 10). Null takes the factory's
  // default on the server — Hindi where none is set. Pinned on the version.
  const [language, setLanguage] = useState<'hi' | 'en' | null>(null)
  /**
   * What the officer wants of this drafting, in their own words.
   *
   * Cleared on every successful generation rather than kept: an instruction
   * belongs to the version it produced — it is recorded on that row — and one
   * left standing in the box would silently ride the next drafting, which is
   * the officer's words being applied to a document they did not mean them for.
   */
  const [instruction, setInstruction] = useState('')
  // Stage 4 only: which paper on the file this pleading answers, and what the
  // last draft found about how much of it was answered.
  const [sourcePaper, setSourcePaper] = useState<string | null>(null)
  // Stage 4 as it now works: no document, just the paragraph-wise replies.
  const [drafted, setDrafted] = useState<RepliesView | null>(null)
  const [petitioned, setPetitioned] = useState<GroundsView | null>(null)
  const [asking, setAsking] = useState(false)
  /**
   * Why a selection produced nothing.
   *
   * There are four ways it can, and all four used to be silent: the document is
   * issued, the English view is open, the selection crosses two paragraphs, or
   * a boundary landed inside a blank. An officer who drags across three lines
   * and gets nothing learns only that the tool is unreliable — rule 3 says the
   * refusal stays on screen with its reason, and this is where it lives.
   */
  const [refusal, setRefusal] = useState<{ reason: string; range: Range | null }>({
    reason: '',
    range: null,
  })

  /**
   * **The server's own answer to *mark issued*, kept until its page arrives.**
   *
   * The command succeeds in a moment; the page that says so is the version
   * read again, which can take many seconds. Between the two the screen showed
   * no sign that the act had taken and left *Mark issued* live for an
   * occurrence already issued (walkthrough 32.34) — one more click and the
   * officer is refused for doing what the screen invited. What is held here is
   * the receipt of a command the server committed, for this occurrence only;
   * the moment the version carries its own `issued_on`, that is what is read.
   */
  const [issuedHere, setIssuedHere] = useState<{
    occurrence: number
    on: string
    /** The act as the card worded it — *It has been sent up*. */
    act: string | null
  } | null>(null)

  /** The version *mark issued* wrote with the date just given on its date line,
   *  so the page can say that it did — nothing on a document changes without a
   *  word (rule 2). */
  const [datedOnIssue, setDatedOnIssue] = useState<{ version: string; on: string } | null>(null)

  /** Which document this screen is on, and whether it is still on screen at
   *  all — read by the steps that finish after a round trip to the server. */
  const route = `${caseId ?? ''}/${documentType ?? ''}`
  const here = useRef({ route, mounted: true })
  here.current.route = route
  useEffect(() => {
    here.current.mounted = true
    return () => {
      here.current.mounted = false
    }
  }, [])

  /** The version a draft started on arrival made — so the page can say, on that
   *  version and once, what it was made as (see `arrived`). */
  const [draftedAtOnce, setDraftedAtOnce] = useState<string | null>(null)
  /** From an arrival deciding to start a draft until that request settles. The
   *  hand-pressed button is greyed for exactly that long. */
  const [startingAtOnce, setStartingAtOnce] = useState(false)

  /**
   * The purpose belongs to one document type on one case. The route keeps this
   * component mounted when only the params change, so without this reset a
   * suggestion auto-picked on D01 would ride the next Generate on D02 — where
   * no purposes exist, the server rightly refuses, and no control is on screen
   * to clear the stale value.
   */
  useEffect(() => {
    setPurpose(null)
    setPart(null)
    // And what this screen remembers of its own acts on the document it is
    // leaving: a receipt for one document must not ride onto the next.
    setIssuedHere(null)
    setDraftedAtOnce(null)
    setListingMarks(false)
  }, [caseId, documentType])

  /**
   * A ticking clock, only while something is drafting.
   *
   * The strip above the document counts up between polls, so the number moves
   * every second rather than in 1.5-second jumps. It stops the moment assembly ends: a timer
   * left running would re-render the whole document once a second for as long as
   * the officer had it open.
   */
  const [now, setNow] = useState(() => Date.now())

  const version = useQuery({
    queryKey: ['version', caseId, versionId],
    queryFn: () => api.readVersion(caseId as string, versionId as string),
    enabled: Boolean(caseId && versionId),
    // The page stops asking the moment nothing is owed.
    refetchInterval: (query) => (query.state.data?.drafting ? POLL_INTERVAL_MS : false),
    // **And keeps asking while it is not in front.** Without this the library
    // skips every tick of the interval while the browser reports the page
    // hidden, and the strip went on counting seconds over a draft that was
    // already written (see `POLL_INTERVAL_MS`). A draft is owed whether or not
    // the officer is looking at the window it will arrive in.
    refetchIntervalInBackground: true,
  })

  /**
   * A block arrived at from a chat citation, marked so the officer can see which
   * sentence the answer was drawn from.
   *
   * The mark is put on and left on rather than faded out: an officer who
   * followed a citation, scrolled away and scrolled back has to be able to find
   * the paragraph again without asking twice.
   */
  const citedBlock = params.get('b')
  useEffect(() => {
    if (!citedBlock || !version.data) return
    const target = document.querySelector(`[data-testid="block-${citedBlock}"]`)
    if (!target) return
    target.scrollIntoView({ block: 'center' })
    target.classList.add('cited')
  }, [citedBlock, version.data])

  const drafting = Boolean(version.data?.drafting)
  useEffect(() => {
    if (!drafting) return
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [drafting])

  const versions = useQuery({
    queryKey: ['versions', caseId, documentType],
    queryFn: () => api.listVersions(caseId as string, documentType as string),
    enabled: Boolean(caseId && documentType),
  })

  /**
   * Whether another one may be made, **as the rail says** — never as this screen
   * works out for itself.
   *
   * `_generatability` on the server answers four rules at once: upload-only,
   * authorship, out-of-scope, and whether an officer recorded the document
   * skipped. A screen deciding for itself would be a second copy of them, and
   * the direction it fails in is offering Generate on a document nobody may
   * draft (`web/CLAUDE.md`).
   */
  const caseDetail = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => api.getCase(caseId as string),
    enabled: Boolean(caseId),
  })
  const railRow = (caseDetail.data?.rail ?? []).find((r) => r.document_type === documentType)

  /**
   * **The document's name, as the office calls it** — never its code.
   *
   * The screen was headed `D05`, `D07 v1 · occurrence 1`: the register number
   * of the data model, which no officer was ever asked to learn (walkthrough
   * 32.34). The name is the case's own pinned process version's, and the rail
   * this screen already reads carries it — the register title, in full,
   * because this is where the officer reads the record (docs/16, #12). A
   * document outside the twenty is named by its own row. Nothing is written
   * here (stop list 6); the code shows only if the case names no such document.
   */
  const adhocRow = (caseDetail.data?.other_documents ?? []).find(
    (r) => r.document_type === documentType,
  )
  const documentName =
    railRow?.title ?? adhocRow?.title ?? (caseDetail.isLoading ? '…' : (documentType ?? ''))

  /**
   * The case card's own offer to record this document as gone out, where it is
   * making one. Its label is the act in the office's words — *It has been sent
   * up* for a noting, *It has been issued* for an instrument — and which of the
   * two a document is, only the server knows.
   */
  const issueOffer = (caseDetail.data?.next_action?.offers ?? []).find(
    (offer) => offer.act === 'issue' && offer.document === documentType,
  )

  /*
   * **There is deliberately no auto-adopt effect here.** F-03 put one in and it
   * had to come out, so this is a note against putting it back.
   *
   * The complaint was real: the rail links to `/cases/{id}/documents/{type}`
   * with no `?v`, so arriving at an already-generated document showed the page
   * led by *"No draft is open"* over a document that plainly existed. The fix
   * taken was an effect that adopted the latest version whenever `?v` was
   * absent.
   *
   * That made the Generate button unreachable. It renders only inside
   * `{!versionId ? … }`, so adopting a version on arrival removed the only
   * surface in the app that calls `POST /generate`: a repeatable document
   * (D03, D06) could never be drafted a second time, a recurring one (D10,
   * D11) never again at all, and no document could be regenerated after the
   * record behind it was corrected. Reviewed and reproduced three ways on
   * 03/09/2026.
   *
   * The empty pane already listed every existing version below the button. So
   * the answer was never to open one — it was to stop the pane *reading* as
   * though the document did not exist, which is what it now says instead.
   */

  /**
   * `document_id` is null on a preflight, because a preflight writes nothing and
   * there is no version to point at. Nothing here runs a preflight — and the
   * guard is written rather than cast away, because the day something does, a
   * cast would put `/documents/D06?v=undefined` in the address bar.
   */
  const openVersion = (id: string | null | undefined) => {
    if (!id) return
    // `b` is dropped on purpose: a block anchor belongs to the version it was
    // cited from, and carrying it onto another version would mark whatever
    // paragraph happens to sit at that ordinal.
    setParams({ v: id }, { replace: true })
    setProposal(null)
    setSelection(null)
  }

  // Drafting again keeps what the open draft was for: a noting redrafted
  // without its purpose went down the old path, with no call and the office
  // closing left a blank (run of 26 Sep 2026).
  const draftPurpose = purpose ?? version.data?.purpose ?? null
  const draftPart = part ?? version.data?.part ?? null
  /** What the pickers stand at. A draft started on arrival names its own
   *  choice instead (`arrived`): the pickers' state has not settled yet. */
  const standing: Choice = { purpose: draftPurpose, part: draftPart }
  /** The document a draft was asked for, noted as the request leaves. */
  const draftingFor = useRef<string | null>(null)
  const generate = useMutation({
    mutationFn: (as: Choice | undefined) => {
      const choice = as ?? standing
      draftingFor.current = route
      return api.generateDocument(
        caseId as string,
        documentType as string,
        {
          ...(choice.part ? { part: choice.part } : {}),
          ...(choice.purpose ? { purpose: choice.purpose } : {}),
          ...(language ? { language } : {}),
          ...(instruction.trim() ? { instruction: instruction.trim() } : {}),
        } as never,
      )
    },
    onSuccess: (fresh, as) => {
      client.setQueryData(['version', caseId, fresh.document_id], fresh)
      // The case's card and rail were worked out before this draft existed.
      refreshCase()
      // Gone elsewhere while it was being made: the draft is on the case, and
      // opening it here would pull the officer back to a page they left — or
      // open it under another document's name.
      if (!here.current.mounted || here.current.route !== draftingFor.current) return
      openVersion(fresh.document_id)
      setNow(Date.now())
      void versions.refetch()
      // The instruction belongs to the version it made, and it is on that row.
      // One left in the box would ride the next drafting silently.
      setInstruction('')
      setAsking(false)
      setNeeds(null)
      setNeedsAs(undefined)
      // Said once, on the version it made: this one was started for the
      // officer, as a choice they did not make themselves.
      setDraftedAtOnce(as?.atOnce ? (fresh.document_id ?? null) : null)
    },
    onSettled: () => setStartingAtOnce(false),
  })

  /**
   * **Before drafting, what it is written from and the file lacks** (A11). The
   * draft action asks the server first; where nothing is missing it drafts at
   * once, as it always did, and where something is, the panel asks: upload it,
   * record it, or draft anyway. A check that fails is said and never blocks —
   * the app never refuses to draft.
   */
  const [needs, setNeeds] = useState<DraftingNeedsView | null>(null)
  /** What the question was asked for, so *Draft anyway* drafts that and not
   *  whatever the pickers have moved to since. */
  const [needsAs, setNeedsAs] = useState<Choice | undefined>(undefined)
  const needsFor = (as?: Choice) => {
    const choice = as ?? standing
    return api.draftingNeeds(caseId as string, documentType as string, {
      purpose: choice.purpose,
      part: choice.part,
    })
  }
  const askFirst = useMutation({
    mutationFn: (as: Choice | undefined) => needsFor(as),
    onSuccess: (found, as) => {
      if ((found.needs ?? []).length === 0) return generate.mutate(as)
      setNeeds(found)
      setNeedsAs(as)
      setStartingAtOnce(false)
    },
    onError: () => setStartingAtOnce(false),
  })
  const askAgain = useMutation({ mutationFn: () => needsFor(needsAs), onSuccess: setNeeds })
  const startDrafting = (as?: Choice) => {
    setNeeds(null)
    setNeedsAs(undefined)
    askFirst.mutate(as)
  }

  /* ------------------------------------------------ drafted on arrival ------
   *
   * **The card said *Draft it*; the draft starts.** The link opened a nearly
   * empty page where the officer had to say *Generate* a second time for a
   * choice the page had already made for them (walkthrough 32.34). Arriving
   * from the card's draft offer on a document with no version yet now starts
   * it, as exactly what that page would have had preselected — the purpose the
   * server suggests, the first instrument, the factory's language — and says
   * which, with the way back to choose again.
   *
   * **It can start once.** Four things hold that, each on its own:
   *
   * - the request to start is in the history entry's state and is taken off
   *   that entry the moment it is seen — before anything is asked of the
   *   server — so a reload, or coming back to this address, finds nothing;
   * - it is held after that only in this component's memory, which a reload
   *   does not have;
   * - it acts on the record **read again there and then**, never the cached
   *   one, and does nothing if a version stands — for a noting, if the card
   *   read again no longer offers that noting to draft;
   * - the hand-pressed button is greyed while it runs.
   *
   * Nothing here decides the document may be drafted: the rail says so
   * (`generatable`), and where the file lacks something the draft is written
   * from, the same question is asked as when the button is pressed. Only the
   * card's own *We don't have it — draft anyway* skips that question — it has
   * just been asked, and answered, on the card.
   */
  const [arrived, setArrived] = useState<{
    route: string
    anyway: boolean
    /** The noting the card asked for; null for every other document. */
    purpose: string | null
  } | null>(null)
  const taken = useRef<string | null>(null)
  useEffect(() => {
    if (!isDraftNow(location.state)) return
    // The same entry seen again (React runs an effect twice in development).
    if (taken.current === location.key) return
    taken.current = location.key
    setArrived({
      route,
      anyway: Boolean(location.state.anyway),
      purpose: location.state.purpose ?? null,
    })
    // Off the history entry before anything else happens.
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: null },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per arrival
  }, [location.key])

  // The same forecast the pickers read — one request, by the shared key.
  const forecast = useQuery({
    queryKey: ['variants', caseId, documentType],
    queryFn: () =>
      api.generateDocument(
        caseId as string,
        documentType as string,
        { preflight: true } as never,
      ),
    retry: false,
    enabled: Boolean(arrived && caseId && documentType),
  })
  const started = useRef<string | null>(null)
  useEffect(() => {
    if (!arrived) return
    const stand = () => setArrived(null)
    // Another document, a version already open, or a pleading (which first
    // asks which paper it answers): nothing to start.
    if (arrived.route !== route || versionId || isPleading(documentType)) return stand()
    if (caseDetail.isLoading || forecast.isLoading) return
    // The rail does not offer it, or the forecast could not be read: the page
    // stands as it always did, with its button and the server's reason.
    if (!railRow?.generatable || !forecast.data) return stand()
    // **The noting the card named, where it named one.** A noting is one
    // document drafted for several purposes, so the card that says *send the
    // draft chargesheet up* says which, and that is what is drafted — not the
    // purpose the picker would have stood at, which on the run of 4 Oct was
    // the noting already sent up. Otherwise: what the picker would have had
    // selected on arrival. The part and the language are left for the server
    // to default, as the pickers do.
    const asked = arrived.purpose
    const wanted = asked ?? forecast.data.purpose_suggested ?? null
    const offered = (forecast.data.purposes ?? []).some((p) => p.key === wanted)
    // A noting the pinned template does not declare is not drafted as some
    // other one: the page stands, with its pickers.
    if (asked && !offered) return stand()
    const once = `${route}#${asked ?? ''}`
    if (started.current === once) return stand()
    started.current = once

    const choice: Choice = { purpose: offered ? wanted : null, part: null, atOnce: true }
    const anyway = arrived.anyway
    setArrived(null)
    setStartingAtOnce(true)
    void Promise.all([versions.refetch(), asked ? caseDetail.refetch() : null])
      .then(([latest, detail]) => {
        const still = here.current.mounted && here.current.route === route
        // **Whether one already stands, read again there and then.** For a
        // noting that is the server's word, not this screen's count: the list
        // holds every noting ever drafted on the case, so it is the card, read
        // again, that says whether *this* one is still to draft.
        const stands = asked
          ? !(detail?.data?.next_action?.offers ?? []).some(
              (offer) =>
                offer.act === 'draft' &&
                offer.document === documentType &&
                offer.purpose === asked,
            )
          : (latest.data ?? []).length > 0
        // Left the page, could not read the record, or one stands: nothing is
        // started, and the page is as it always was.
        if (!still || latest.isError || detail?.isError || stands) {
          if (here.current.mounted) setStartingAtOnce(false)
          return
        }
        setPurpose(choice.purpose)
        if (anyway) generate.mutate(choice)
        else startDrafting(choice)
      })
      .catch(() => {
        if (here.current.mounted) setStartingAtOnce(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- acts when what it waits on has arrived
  }, [arrived, route, versionId, caseDetail.isLoading, forecast.isLoading, forecast.data])

  /**
   * Stage 4's other posture — the department's own petition.
   *
   * **`[D]`, and it stays flagged.** No AWEIL sample and no prescribed form
   * stand behind a machine-drafted writ ground, which is exactly what that
   * layer means; stop list 8 says the flag comes off when the office has
   * settled the form, not when the page looks finished.
   */
  const draftGrounds = useMutation({
    mutationFn: () =>
      api.draftGrounds(
        caseId as string,
        documentType as string,
        {
          source_file_id: sourcePaper,
          ...(instruction.trim() ? { instruction: instruction.trim() } : {}),
        } as never,
      ),
    onSuccess: (fresh) => {
      setPetitioned(fresh)
      if (fresh.document) {
        client.setQueryData(['version', caseId, fresh.document.document_id], fresh.document)
        openVersion(fresh.document.document_id)
        setNow(Date.now())
        void versions.refetch()
      }
      setInstruction('')
    },
  })

  /**
   * Stage 4 — the paragraph-wise replies.
   *
   * **No document is written.** The office has its counter affidavit in Word;
   * what it cannot do quickly is answer thirty-eight paragraphs of a sixty-page
   * application.
   *
   * Two ways, and the answer says which. Stage 4 on Sarvam returns at once with
   * `drafting` true and every reply pending: a background job writes them over
   * about twelve minutes, the version query above polls while `drafting` holds,
   * and the workspace reads its progress off `drafting_usage.court`. The earlier
   * way runs the model calls inside the request and returns the finished set,
   * and then nothing polls because nothing is owed.
   */
  const draftReplies = useMutation({
    mutationFn: () =>
      api.draftReplies(
        caseId as string,
        documentType as string,
        {
          source_file_id: sourcePaper,
          ...(instruction.trim() ? { instruction: instruction.trim() } : {}),
        } as never,
      ),
    onSuccess: (fresh) => {
      setDrafted(fresh)
      // The replies are a version like any other: it goes on the rail, it
      // renders here, and it downloads through the exporter every other
      // document already uses.
      if (fresh.document) {
        client.setQueryData(['version', caseId, fresh.document.document_id], fresh.document)
        openVersion(fresh.document.document_id)
        setNow(Date.now())
        void versions.refetch()
      }
      setInstruction('')
    },
  })

  // Records that a person looked — not that the value is right. The server
  // returns the whole set, so the countdown comes back rather than being
  // recomputed here and disagreeing with it.
  const checkMark = useMutation({
    mutationFn: (markKey: string) =>
      api.checkDocumentMark(caseId as string, versionId as string, markKey),
    onSuccess: () => {
      setOpenMark(null)
      void version.refetch()
    },
  })

  const fill = useMutation({
    mutationFn: ({ slot, value }: { slot: string; value: string }) =>
      api.fillBlanks(
        caseId as string,
        versionId as string,
        { values: { [slot]: value } } as never,
        `"${version.data?.content_hash ?? ''}"`,
      ),
    onSuccess: (fresh) => {
      client.setQueryData(['version', caseId, fresh.document_id], fresh)
      openVersion(fresh.document_id)
      void versions.refetch()
      /* **On to the next blank, without the officer going to look for it.**
       *
       * Filling was: find an amber chip somewhere in a two- or three-page
       * document, click it, type in a drawer laid over the document, save, then
       * scan the whole document again for the next one. Seven blanks meant
       * seven passes over the paper. Now Enter writes the value and lands in
       * the next gap, in the order they appear on the page.
       *
       * Read from the DOM rather than from `unresolved`, because the officer's
       * order is the document's order and the server's array is not obliged to
       * match it. After the state settles: the blocks re-render on the fresh
       * version, and a slot queried before that is the old paint. */
      setFilling(null)
      window.setTimeout(() => {
        const gaps = Array.from(
          document.querySelectorAll<HTMLElement>('.workbench .draft .blank[data-slot]'),
        )
        const next = gaps.find((el) => !el.classList.contains('unfillable'))
        const slot = next?.dataset['slot']
        if (!slot) return
        const chip = (fresh.unresolved ?? []).find((c) => c.slot === slot)
        if (chip && chip.fillable !== false && !chip.is_list) setFilling(chip)
      }, 60)
    },
  })

  const edit = useMutation({
    mutationFn: async ({ blockKey, text }: { blockKey: string; text: string }) => {
      const args = [
        caseId as string,
        versionId as string,
        { block_key: blockKey, text_hi: text } as never,
        `"${version.data?.content_hash ?? ''}"`,
      ] as const
      if (isAnsweredParawise(documentType)) {
        return { kind: 'proposal' as const, value: await api.proposeManualEdit(...args) }
      }
      return { kind: 'version' as const, value: await api.editBlock(...args) }
    },
    onSuccess: (result) => {
      setEditing(null)
      if (result.kind === 'proposal') {
        void client.invalidateQueries({ queryKey: ['reply-evidence', caseId, versionId] })
        return
      }
      const fresh = result.value
      client.setQueryData(['version', caseId, fresh.document_id], fresh)
      openVersion(fresh.document_id)
      void versions.refetch()
    },
  })

  const issue = useMutation({
    mutationFn: (went: { on: string; occurrence: number; act: string | null }) =>
      api.markIssued(
        caseId as string,
        {
          document_type: documentType as string,
          occurrence: went.occurrence,
          issued_on: went.on,
        } as never,
      ),
    onSuccess: (receipt, went) => {
      // Said at once, from the command's own success — see `issuedHere`.
      setIssuedHere(went)
      // **The document that went out is the one on screen.** Where the draft's
      // date line did not already read the date just given, the server wrote a
      // successor that does and names it in its receipt. Staying on the draft
      // left the page reading the old date over a document recorded as issued,
      // with *Download* and the signed copy both still pointed at the old one
      // (compliance review, 4 Oct 2026).
      const dated = (receipt.detail as Record<string, unknown> | undefined)?.dated_version
      if (typeof dated === 'string' && here.current.mounted) {
        setDatedOnIssue({ version: dated, on: went.on })
        openVersion(dated)
      } else {
        void version.refetch()
      }
      void versions.refetch()
      // And the case's card, which was offering this same act a moment ago.
      refreshCase()
    },
  })

  const propose = useMutation({
    mutationFn: (instruction: string) =>
      api.proposeRewrite(
        caseId as string,
        versionId as string,
        {
          block_key: String(selection?.block.order ?? ''),
          span_start: selection?.start ?? 0,
          span_end: selection?.end ?? 0,
          instruction,
        } as never,
      ),
    onSuccess: (made) => setProposal(made),
  })

  const accept = useMutation({
    mutationFn: () =>
      api.acceptProposal(
        caseId as string,
        proposal?.proposal_id as string,
        `"${version.data?.content_hash ?? ''}"`,
      ),
    onSuccess: (fresh) => {
      client.setQueryData(['version', caseId, fresh.document_id], fresh)
      openVersion(fresh.document_id)
      void versions.refetch()
    },
  })

  const reject = useMutation({
    mutationFn: () => api.rejectProposal(caseId as string, proposal?.proposal_id as string),
    // Nothing on the document changed, so nothing is refetched. That is the
    // point of Reject: no version, no trace.
    onSuccess: () => {
      setProposal(null)
      setSelection(null)
    },
  })

  const englishView = useQuery({
    queryKey: ['english', caseId, versionId],
    queryFn: () => api.englishView(caseId as string, versionId as string),
    enabled: english && Boolean(caseId && versionId),
  })

  const data = version.data
  const assembly = useMemo(() => (data ? readAssembly(data, now) : null), [data, now])
  /**
   * **The server's blocking set, not this screen's.**
   *
   * It used to be `checks.filter(severity === 'BLOCK' && !passed)`, which is a
   * second copy of a rule the server owns and a wrong one: it counted advisory
   * rows, which nobody decided, and rows enforced at a later transition, which
   * stop the case moving rather than the document issuing. On D10 that greys the
   * download on a lawful hearing notice — the exact act `CR-14-20`'s own comment
   * says must not be refused. `web/CLAUDE.md`: the client never decides what is
   * possible.
   */
  const blocking = (data?.blocking ?? []).map((c) => ({
    rule: c.rule ?? '',
    text: c.text,
    citation: c.citation,
  }))
  // Issued as the version says — or, until that page arrives, as the server
  // answered this screen's own *mark issued* for this occurrence.
  const issuedNow =
    issuedHere && data && issuedHere.occurrence === data.occurrence ? issuedHere : null
  const issuedOn = data?.issued_on ?? issuedNow?.on ?? null
  const issued = Boolean(issuedOn)
  // One wording for the fact wherever this page states it. The card's words
  // for the act where this visit recorded it; the plain word otherwise.
  const issuedWords = issuedOn
    ? issuedNow?.act
      ? `${issuedNow.act} — ${readDate(issuedOn)}`
      : `Issued ${readDate(issuedOn)}`
    : null
  // The card's words for recording it as gone out, where the card is offering
  // that for this very one.
  const issueAct =
    data && issueOffer && (issueOffer.occurrence ?? data.occurrence) === data.occurrence
      ? issueOffer.label
      : null
  // Which of several this is, in words — and nothing where there is only one.
  const which = data ? readOccurrence(data.occurrence, railRow?.occurrences) : null
  const purposeWords = data?.purpose
    ? (data.purposes ?? []).find((p) => p.key === data.purpose)?.label || data.purpose
    : null
  /** How many paragraphs an instruction can actually reach. `[C]` is the only
   *  provenance a model writes; everything else is locked, fixed or substituted. */
  const modelWritten = (data?.blocks ?? []).filter((b) => b.provenance === 'C').length
  const readOnly = issued || english

  // A court reply is named by its number and its name, off the case's own
  // process version — the server's words, never a title held here.
  const courtRow = useQuery({
    queryKey: ['court-documents', caseId],
    queryFn: () => api.readCourtDocuments(caseId as string),
    enabled: Boolean(caseId) && isAnsweredParawise(documentType ?? ''),
    retry: false,
  }).data?.documents?.find((row) => row.document_type === (documentType ?? '').toUpperCase())
  const courtTitle = courtRow
    ? `${courtRow.number ? `${courtRow.number} · ` : ''}${courtRow.title}`
    : null

  return (
    <div
      className={`page workbench ${isAnsweredParawise(documentType) ? 'd18-workbench' : ''}`}
    >
      {/* **One band, not four.**
       *
       * A breadcrumb, a title, a meta line and a toolbar were four horizontal
       * strips above the document doing the work of about two — and on a 900px
       * laptop they were the difference between a short document fitting on one
       * screen and not. Identity on the left, the two things worth knowing in
       * the middle, the acts on the right, all on one baseline. */}
      <header className="docbar">
        <div className="who">
          {/* **Back goes where you came from.** A version is a `?v=` on this
              same route, so an open document and the list that offered it are
              two different URLs — and back pointed at the case from both,
              throwing an officer who had opened v2 to compare it with v1 all
              the way out to the rail. From an open version it returns to this
              document; from the document it returns to the case. */}
          <Link
            to={versionId ? `/cases/${caseId}/documents/${documentType}` : `/cases/${caseId}`}
            className="back"
            title={versionId ? 'Back to this document' : 'Back to the case'}
          >
            ‹
          </Link>
          {/* The name, then which draft of it. A noting is written at every
              stage, so what this one is for is part of what it is called. */}
          <h1 data-testid="document-name">
            {courtTitle ?? documentName}
            {data ? (
              <span className="v" data-testid="draft-number">
                {' '}
                Draft {data.version}
              </span>
            ) : null}
          </h1>
          {purposeWords || which ? (
            <span className="meta">
              {purposeWords ? <span data-testid="purpose-chip">{purposeWords}</span> : null}
              {purposeWords && which ? ' · ' : null}
              {which ? <span data-testid="which-one">{which}</span> : null}
            </span>
          ) : null}
          {issued && data ? (
            <span className="chip good">
              {/* A court reply is signed, not issued: it goes to counsel, and
                  what is filed is uploaded afterwards (decision 33). */}
              {isAnsweredParawise(documentType) && issuedOn
                ? `Signed ${readDate(issuedOn)}`
                : issuedWords}
              {data.serial_no ? ` · serial ${data.serial_no}` : ''}
            </span>
          ) : null}
        </div>

        {data && versionId ? (
          <>
            {/* **How things stand, apart from what you can do about it.**
                These two are facts and the three beside them are acts, and
                they sat on one line at one weight — so "6 blanks remaining"
                read like a button and Download read like a status. Separated
                by a rule rather than by colour, because colour on this screen
                is already carrying state. */}
            {!isAnsweredParawise(documentType) ? (
              <div className="state">
                <BlankCounter remaining={data.blanks_remaining} />
                <MarkCounter
                  marks={data.marks ?? null}
                  open={listingMarks}
                  onToggle={() => setListingMarks((on) => !on)}
                />
                <UnsourcedCount blocks={data.blocks ?? []} />
              </div>
            ) : null}
            <div className="acts">
              {!isAnsweredParawise(documentType) ? (
                <>
                  <button
                    type="button"
                    className={english ? 'btn' : 'quiet'}
                    onClick={() => setEnglish((on) => !on)}
                    data-testid="english-toggle"
                  >
                    {english ? 'Hindi (the document)' : 'English reference'}
                  </button>
                  <DownloadButton
                    caseId={caseId as string}
                    versionId={versionId as string}
                    blocking={blocking}
                  />
                </>
              ) : null}
              {/* **Another one, from where the officer is standing.**
               *
               * Generate lived only on the empty state — the same route with no
               * `?v`. So an officer reading an issued D01 who wants a second one
               * had to go back to the case and find the row again, and nothing on
               * this screen said so. D01 is RECURRING; a second occurrence is
               * ordinary, not an edge case.
               *
               * Where the rail refuses, the control stays on screen with the
               * reason rather than vanishing — rule 3, and a button that
               * disappears teaches nothing. */}
              {railRow ? (
                railRow.generatable || isAnsweredParawise(documentType) ? (
                  <button
                    type="button"
                    className="quiet"
                    onClick={() => {
                      if (isAnsweredParawise(documentType)) {
                        setParams({}, { replace: true })
                        setSourcePaper(null)
                        setInstruction('')
                      } else {
                        setAsking((on) => !on)
                      }
                    }}
                    disabled={generate.isPending || draftReplies.isPending}
                    data-testid="generate-again"
                    title={
                      isAnsweredParawise(documentType)
                        ? 'Choose the paper and draft a new version. The version you are reading is kept.'
                        : issued
                          ? 'Draft the next one. What was issued is untouched — a new one is started, and an issued document is never written over.'
                          : 'Draft this document again. The version you are reading is kept; nothing is ever deleted.'
                    }
                  >
                    {isAnsweredParawise(documentType)
                      ? 'Draft another version'
                      : generate.isPending
                        ? 'Assembling…'
                        : issued
                          ? 'Draft the next one'
                          : 'Draft it again'}
                  </button>
                ) : (
                  <span className="blocked inline" data-testid="generate-again-blocked">
                    <button type="button" className="quiet" disabled>
                      {issued ? 'Draft the next one' : 'Draft it again'}
                    </button>
                    <span className="why">{railRow.not_generatable_reason}</span>
                  </span>
                )
              ) : null}
            </div>
          </>
        ) : null}
      </header>

      {/* Phase A, Lane C: his particulars corrected after this version was made. */}
      {caseId && versionId ? (
        <ParticularsChangedNote caseId={caseId} versionId={versionId} />
      ) : null}

      {/* **Which choice a draft started on arrival was made as** — said on the
          version it made and nowhere else, with the way back to choose again.
          The officer did not make this choice; the page made it for them, so
          it says which it was. Nothing is shown where the document has no
          choice to make. */}
      {data && versionId && draftedAtOnce === versionId && !issued ? (
        <DraftedAtOnce
          purpose={purposeWords}
          part={data.part_mode === 'choice' ? (data.part ?? null) : null}
          partLabels={data.part_labels}
          onChooseAgain={() => {
            // Back to this document's own page: the pickers, and *Generate*.
            setDraftedAtOnce(null)
            setParams({}, { replace: true })
          }}
        />
      ) : null}

      {/* The list the counter opens: every mark with its reason, in the
          document's order. Choosing one goes to its words and opens its panel. */}
      {listingMarks && data?.marks && versionId && !isAnsweredParawise(documentType) ? (
        <MarkList
          marks={data.marks}
          onClose={() => setListingMarks(false)}
          onChoose={(mark) => {
            setEnglish(false)
            setOpenMark(mark)
            // After the paint: the words may only just have come back on screen.
            window.setTimeout(() => {
              const at = Array.from(
                document.querySelectorAll<HTMLElement>('.workbench .draft [data-mark]'),
              ).find((el) => el.dataset['mark'] === mark.key)
              at?.scrollIntoView({ block: 'center' })
            }, 60)
          }}
        />
      ) : null}

      {/* **The officer's own ask, for the whole document.**
       *
       * Select-and-instruct changes one passage and passes the diff gate. This
       * is the other half: the shape of the whole thing — shorter, lead with
       * the second allegation, less formal — and it produces a NEW VERSION
       * rather than an edit. Nothing is overwritten, the version being read
       * stays readable, and the instruction is recorded on the row it made, so
       * the file says why two versions of a noting differ.
       *
       * The sentence about what it can reach is not a hint. An instruction only
       * touches paragraphs a model writes: statutory text is locked, office
       * standing text is fixed, and a record-filled value is substituted before
       * any model runs. An officer who types "change the heading" and watches
       * nothing happen learns that the feature is broken, which it is not.
       */}
      {/* **Stage 4: did this reply deal with the whole application.**
       *
       * It sits above the document rather than under it, because Rule 12(2) is
       * about what the pleading as a whole does and an officer who reads to the
       * foot before learning that paragraph 14 is unanswered has read the wrong
       * document carefully. It reports and never blocks — the draft is written
       * either way, and the officer decides.
       *
       * Cleared when he opens a different version: the count belongs to the
       * draft it was measured on, and carrying it onto another version would be
       * a number about a document nobody is looking at. */}
      {/* What the replies do not say on their own: which paragraph nothing
          answered, and which reply is the application rather than an answer to
          it. It sits above the document because it is read before the document,
          and it belongs to the open version rather than to the last draft. */}
      {isAnsweredParawise(documentType) && data && versionId && caseId ? (
        <D18Workspace
          caseId={caseId}
          documentType={documentType as string}
          versionId={versionId}
          data={data}
          onVersion={openVersion}
          onEdit={issued ? undefined : setEditing}
        />
      ) : null}
      {!isAnsweredParawise(documentType) &&
      drafted &&
      data &&
      drafted.document?.document_id === versionId ? (
        <Replies
          numbered={drafted.numbered ?? ''}
          answered={(drafted.replies ?? []).length}
          labels={drafted.labels ?? []}
          missing={drafted.missing ?? []}
          copied={drafted.copied ?? []}
          failures={drafted.failures ?? []}
        />
      ) : null}

      {petitioned && data && petitioned.document?.document_id === versionId ? (
        <Grounds
          lettered={petitioned.lettered ?? ''}
          drafted={(petitioned.grounds ?? []).length}
          grounds={petitioned.grounds ?? []}
          holdings={petitioned.holdings ?? []}
          unattacked={petitioned.unattacked ?? []}
          failures={petitioned.failures ?? []}
        />
      ) : null}

      {asking && data && !isAnsweredParawise(documentType) ? (
        <div className="ask" data-testid="ask-panel">
          <label className="small" htmlFor="ask">
            What should be different this time?
          </label>
          <textarea
            id="ask"
            rows={2}
            value={instruction}
            placeholder="e.g. shorter, and lead with the job cards rather than the absence"
            onChange={(e) => setInstruction(e.target.value)}
            disabled={generate.isPending}
            data-testid="ask-instruction"
          />
          <p className="small faint">
            This reaches only the paragraphs a model writes — {modelWritten} of{' '}
            {(data.blocks ?? []).length} here. Statutory text is locked, the office's standing
            text is fixed, and anything filled from the case record is substituted before a
            model runs. It can ask for a shape, never for a fact the papers do not carry.
          </p>
          <div className="row">
            <button
              type="button"
              className="btn"
              onClick={() => startDrafting()}
              disabled={generate.isPending || askFirst.isPending}
              data-testid="ask-generate"
            >
              {generate.isPending
                ? 'Assembling…'
                : issued
                  ? 'Draft the next one'
                  : 'Draft it again'}
            </button>
            <button
              type="button"
              className="quiet"
              onClick={() => {
                setAsking(false)
                setInstruction('')
              }}
            >
              Cancel
            </button>
            <ErrorEnvelope error={generate.error} />
          </div>
        </div>
      ) : null}

      {needs && !isAnsweredParawise(documentType) ? (
        <DraftingNeedsPanel
          caseId={caseId as string}
          needs={needs}
          // As what the question was asked for — a draft started on arrival
          // keeps the choice the page made, and still says which it was.
          onDraft={() => generate.mutate(needsAs)}
          onCancel={() => {
            setNeeds(null)
            setNeedsAs(undefined)
          }}
          onChanged={() => askAgain.mutate()}
          drafting={generate.isPending}
          draftLabel={
            versionId ? (issued ? 'Draft the next one' : 'Draft it again') : 'Generate'
          }
        />
      ) : null}
      {askFirst.error ? (
        <div className="card" data-testid="drafting-needs-unchecked">
          <div className="body">
            <p className="small">
              What this draft is written from could not be checked. It can still be drafted;
              anything it lacks is marked in the draft.
            </p>
            <ErrorEnvelope error={askFirst.error} />
            <button
              type="button"
              className="btn"
              onClick={() => {
                const as = askFirst.variables
                askFirst.reset()
                generate.mutate(as)
              }}
              disabled={generate.isPending}
            >
              Draft anyway
            </button>
          </div>
        </div>
      ) : null}

      {!versionId ? (
        <div className="card">
          <div className="body">
            {/* **What the officer arrived at, not what is missing.** The rail
                links here with no `?v`, so this pane is what an officer sees
                when they click a document that already exists — and leading it
                with "No draft is open" reads as *this document has not been
                written*, over a document that has. It says how many versions
                stand, and the list of them is directly below (F-03). */}
            {versions.data?.length || startingAtOnce || !isAnsweredParawise(documentType) ? (
              <p className="muted" data-testid="no-version-says">
                {startingAtOnce
                  ? 'Starting the draft…'
                  : versions.data?.length
                    ? `${versions.data.length} version${versions.data.length === 1 ? '' : 's'} of this document ${versions.data.length === 1 ? 'stands' : 'stand'}. Open one below, or generate another.`
                    : 'No draft is open.'}
              </p>
            ) : null}
            {/* **Stage 4 asks a different first question.** A tribunal
                pleading answers a document that arrived in the post, so before
                anything can be drafted the officer has to say which paper that
                is. The variant, purpose and language pickers are not offered:
                these four have one instrument each and the Template Library
                fixes them as English. */}
            {isPetition(documentType) ? (
              <>
                <GroundsPanel
                  caseId={caseId as string}
                  documentType={documentType as string}
                  instruction={instruction}
                  onInstruction={setInstruction}
                  chosen={sourcePaper}
                  onChoose={setSourcePaper}
                  onDraft={() => draftGrounds.mutate()}
                  pending={draftGrounds.isPending}
                />
                <ErrorEnvelope error={draftGrounds.error} />
              </>
            ) : isPleading(documentType) && !isAnsweredParawise(documentType) ? (
              /* A pleading that is neither answered nor petitioned here. Said
                 plainly rather than shown as a button that refuses: rule 3. */
              <p className="small muted" data-testid="not-answered-parawise">
                This document is not drafted here. It is settled with counsel and uploaded
                through <strong>Papers</strong>.
              </p>
            ) : isPleading(documentType) ? (
              <>
                <RepliesPanel
                  caseId={caseId as string}
                  documentType={documentType as string}
                  instruction={instruction}
                  onInstruction={setInstruction}
                  chosen={sourcePaper}
                  onChoose={(id) => {
                    setSourcePaper(id)
                  }}
                  onDraft={() => draftReplies.mutate()}
                  pending={draftReplies.isPending}
                  /* The refusal is shown by the panel, beside the button it
                     greys — rule 3 — rather than under it. */
                  error={draftReplies.error}
                  onClearError={() => draftReplies.reset()}
                />
              </>
            ) : (
              <>
                <VariantChoice
                  caseId={caseId as string}
                  documentType={documentType as string}
                  chosen={part}
                  onChoose={setPart}
                />
                <PurposeChoice
                  caseId={caseId as string}
                  documentType={documentType as string}
                  chosen={purpose}
                  onChoose={setPurpose}
                />
                <LanguageChoice
                  caseId={caseId as string}
                  documentType={documentType as string}
                  chosen={language}
                  onChoose={setLanguage}
                />
                {/* Greyed while a draft is being started for the officer on
                    arrival, so the same draft cannot be asked for twice. */}
                <button
                  type="button"
                  className="btn"
                  onClick={() => startDrafting()}
                  disabled={generate.isPending || askFirst.isPending || startingAtOnce}
                  data-testid="generate"
                >
                  {generate.isPending
                    ? 'Assembling…'
                    : askFirst.isPending
                      ? 'Checking the file…'
                      : startingAtOnce
                        ? 'Starting the draft…'
                        : 'Generate'}
                </button>
                <ErrorEnvelope error={generate.error} />
              </>
            )}
            {versions.data?.length ? (
              <p className="small muted" style={{ marginTop: 12 }}>
                or open an existing version below.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {version.error ? <ErrorEnvelope error={version.error} /> : null}

      {/* The version list stands whether or not a draft is open. Nothing is ever
          deleted, so it is the document's whole history — and the empty state
          above points at it, which it could not do while it only rendered
          alongside an open draft. */}
      {!versionId ? (
        <VersionList versions={versions.data ?? []} currentId="" onOpen={openVersion} />
      ) : null}

      {data ? (
        <>
          {/* The waiting posture. A strip across the top, and the page beneath
              it entirely usable. Never a modal, never a spinner over the whole
              document. */}
          {/* A court reply drafted in the background says how far it has got
              in its own words, inside its workspace: this strip's two-minute
              note is about one paragraph of an ordinary document, and is wrong
              for a twelve-minute draft. */}
          {assembly?.headline && !data.drafting_usage?.court ? (
            <div
              className={assembly.stalled ? 'assembling stalled' : 'assembling'}
              data-testid={assembly.stalled ? 'assembling-stalled' : 'assembling'}
            >
              <strong>
                {assembly.stalled ? 'Nothing is writing these paragraphs.' : assembly.headline}
              </strong>
              {/* Amber, and the reassuring note replaced rather than added to.
                  An officer told "a case paragraph can take two minutes" waits
                  two, then ten, and downloads a document of ruled blanks —
                  which is what happened on 30 Aug 2026. */}
              {assembly.note ? (
                <div className={assembly.stalled ? 'small' : 'small muted'}>
                  {assembly.note}
                </div>
              ) : null}
            </div>
          ) : null}

          {/* **What deserves suspicion, beside what is merely missing.**
           *
           * A blank counter says how much is unfinished. It does not say what is
           * *unsafe*, and those are different questions — the more so once the
           * model fills the values and the counter reaches zero on its own.
           *
           * `[D]` is our own draft with no AWEIL sample and no prescribed form
           * behind it, and stop list 8 says it must be visibly flagged and
           * cleared before real use. Until now it was visible only as a badge in
           * each paragraph's own gutter, so a document could carry six of them
           * and say so nowhere an officer would look first. D07a in the demo
           * fixture is exactly that document: the skeleton has zero AWEIL
           * samples behind it.
           */}
          {issued && !isAnsweredParawise(documentType) ? (
            /* **A state, not a failure, and not news either.** This wore
               `.envelope` — the red, left-barred box the app uses for a
               refusal — so the officer's own act, working exactly as intended,
               answered them in the colour of an error. A green box of the same
               shape was still a banner for four words. It is a line. */
            <div className="issued-line" role="status" data-testid="issued-notice">
              {issuedWords}
              {/* Recorded on this visit: the act took, and the page says so
                  at once rather than when the version is next read. */}
              {issuedNow ? '. Recorded.' : ''}
              {datedOnIssue && datedOnIssue.version === versionId ? (
                <span data-testid="dated-on-issue">
                  {' '}
                  Its date line now reads {readDate(datedOnIssue.on)}, the date you gave. The
                  draft as it stood is kept as the version before this one.
                </span>
              ) : null}
            </div>
          ) : null}

          {!isAnsweredParawise(documentType) ? (
            /* A plain box. It was a `<details open>` with no `<summary>`, for
               which a browser supplies one of its own — the word *Details* and
               a triangle above every document, one click from folding the
               document away. */
            <div className="full-reply-editor">
              {english ? (
                <EnglishPane
                  blocks={englishView.data?.blocks ?? []}
                  unavailable={
                    englishView.error
                      ? englishView.error instanceof ApiFailure
                        ? englishView.error.body.error
                        : 'The translation could not be read.'
                      : englishView.isLoading
                        ? null
                        : (englishView.data?.blocks ?? []).length === 0
                          ? 'No reference translation is available. Hindi is the document of record; this view is a reading of it.'
                          : null
                  }
                />
              ) : (
                <div className="paperwell">
                  <DraftView
                    version={data}
                    caseId={caseId as string}
                    openMark={openMark?.key ?? null}
                    readOnly={readOnly}
                    onFill={setFilling}
                    /* Inline for a gap; the drawer for the two cases that are
                 refusals rather than gaps — a list, and a gap that is not the
                 officer's to fill. Those owe a reason on screen (rule 3), and
                 a text box is not one. */
                    activeSlot={
                      filling && filling.fillable !== false && !filling.is_list
                        ? filling.slot
                        : null
                    }
                    onCommit={(slot, value) => fill.mutate({ slot, value })}
                    onMark={(mark) => setOpenMark(mark)}
                    onCancelFill={() => setFilling(null)}
                    busy={fill.isPending}
                    onRefuse={(reason, range) => setRefusal({ reason, range: range ?? null })}
                    readOnlyReason={
                      issued
                        ? 'This document was issued. It is closed against every editing path — a correction is a new occurrence, never a change to what was served.'
                        : english
                          ? 'The English view is a reading of the document, not the document. Switch to Hindi to change anything.'
                          : null
                    }
                    onSelect={(block, start, end, range) => {
                      setProposal(null)
                      setRefusal({ reason: '', range: null })
                      setSelection({
                        block,
                        start,
                        end,
                        text: block.text_hi.slice(start, end),
                        range,
                      })
                    }}
                  />
                </div>
              )}
            </div>
          ) : null}

          {/* ADR-025. The reason sits beside the words it is about, the same
              posture as the instruct bar: the answer belongs where the question
              was asked. Not a dialog — a dialog over a document is a thing you
              dismiss to see what it is talking about. */}
          {openMark ? (
            <MarkPanel
              mark={openMark}
              busy={checkMark.isPending}
              onCheck={() => checkMark.mutate(openMark.key)}
              onClose={() => setOpenMark(null)}
            />
          ) : null}

          {/* **A selection that produced nothing says why.**
           *
           * Four paths used to return in silence — issued, English, a selection
           * across two paragraphs, a boundary inside a blank — and an officer
           * who drags across three lines and gets nothing learns only that the
           * tool is unreliable. The reason stands where the instruct bar would
           * have been, so the answer is where the question was asked. */}
          {refusal.reason && !selection ? (
            <Beside range={refusal.range}>
              <div className="instruct locked" data-testid="selection-refused">
                <div className="envelope" role="status">
                  {refusal.reason}
                </div>
                <button
                  type="button"
                  className="quiet"
                  onClick={() => setRefusal({ reason: '', range: null })}
                >
                  Close
                </button>
              </div>
            </Beside>
          ) : null}

          {/* Anchored beside the selected passage — see `AtSelection` for why
              this is not rendered after the document, and why the bar no longer
              quotes back the words the officer can see highlighted. */}
          {selection && !english ? (
            <AtSelection range={selection.range}>
              {selection.block.provenance === 'S' || selection.block.locked ? (
                <StatutoryRefusal
                  block={selection.block}
                  onDismiss={() => setSelection(null)}
                />
              ) : proposal ? (
                <DiffCard
                  proposal={proposal}
                  busy={accept.isPending || reject.isPending}
                  error={accept.error ?? reject.error}
                  onAccept={() => accept.mutate()}
                  onReject={() => reject.mutate()}
                  onRetry={() => {
                    setProposal(null)
                    propose.mutate(lastInstruction)
                  }}
                />
              ) : (
                <InstructBar
                  selection={selection}
                  busy={propose.isPending}
                  error={propose.error}
                  onDismiss={() => setSelection(null)}
                  onEdit={() => {
                    setEditing(selection.block)
                    setSelection(null)
                  }}
                  onAsk={(instruction) => {
                    setLastInstruction(instruction)
                    propose.mutate(instruction)
                  }}
                />
              )}
            </AtSelection>
          ) : null}

          {!isAnsweredParawise(documentType) ? (
            <PreIssuePanel
              checks={data.checks ?? []}
              blocking={data.blocking ?? []}
              drafting={Boolean(data.drafting)}
              unresolved={data.unresolved ?? []}
              onGoToBlank={(slot) => {
                const chip = (data.unresolved ?? []).find((c) => c.slot === slot)
                if (chip) setFilling(chip)
                document
                  .querySelector(`[data-slot="${slot}"]`)
                  ?.scrollIntoView({ block: 'center' })
              }}
            />
          ) : null}

          {/* **About this document** — everything that is machinery rather
              than the document: which template made it, which occurrence it is,
              its address, and every version that has stood. All of it is real
              and none of it is what an officer came here to read, so it is one
              disclosure rather than two panels and a header line.

              The version list used to render twice on this route — once on the
              empty state and again under the document. Once is right, and this
              is the place, because reading an earlier version is a thing you go
              looking for rather than something the screen should offer while
              you are reading this one. */}
          {!isAnsweredParawise(documentType) ? (
            /* **The fold is gone and so are two of its three rows.**
               Occurrence is on the header line two centimetres above, and the
               content hash is an address for the machine, not a fact about the
               proceeding. What stays here is what an officer acts on: what the
               draft was written without, the signed copy, and the versions —
               which were buried behind a disclosure titled "About this
               document". The pinned template and the drafting receipt are the
               machinery, and are one disclosure at the foot of the page
               (`HowItWasMade`). */
            <div data-testid="about-document">
              <DraftedWithout call={data.drafting_call ?? null} />
              {data.document_id && caseId && documentType ? (
                <SignedCopyAttach
                  caseId={caseId}
                  documentType={documentType}
                  versionId={data.document_id}
                  onRecorded={() => {
                    void client.invalidateQueries({ queryKey: ['version', caseId, versionId] })
                    void client.invalidateQueries({ queryKey: ['case', caseId] })
                  }}
                />
              ) : null}
              <VersionList
                versions={versions.data ?? []}
                currentId={data.document_id ?? ''}
                onOpen={openVersion}
              />
            </div>
          ) : (
            <details className="about" data-testid="d18-version-history">
              <summary>Version history</summary>
              <div className="about-body">
                <VersionList
                  versions={versions.data ?? []}
                  currentId={data.document_id ?? ''}
                  onOpen={openVersion}
                />
              </div>
            </details>
          )}

          {/* Gone the moment it is issued — by the version's own word, or by
              the server's answer to this screen's own act (`issuedHere`). A
              live *Mark issued* over an occurrence already issued invites a
              second click that can only be refused. */}
          {!issued && !isAnsweredParawise(documentType) ? (
            <IssuePanel
              name={documentName}
              which={which}
              act={issueAct}
              blocking={blocking.length}
              busy={issue.isPending}
              error={issue.error}
              onIssue={(on) => issue.mutate({ on, occurrence: data.occurrence, act: issueAct })}
            />
          ) : null}

          {/* **The machinery, one closed line at the foot of the page.** Which
              template made the document, the drafting call's receipt and what
              the model was sent about the case: all real, all kept, and none of
              it what an officer opened the screen to read. */}
          {!isAnsweredParawise(documentType) ? (
            <HowItWasMade
              call={data.drafting_call ?? null}
              templateVersion={data.template_version ?? null}
              onOpenTemplate={() => setShowTemplate(true)}
            />
          ) : null}
        </>
      ) : null}

      {editing ? (
        <EditBlockDialog
          block={editing}
          reviewBeforeSaving={isAnsweredParawise(documentType)}
          busy={edit.isPending}
          error={edit.error}
          onCancel={() => {
            setEditing(null)
            edit.reset()
          }}
          onSubmit={(text) => edit.mutate({ blockKey: String(editing.order), text })}
        />
      ) : null}

      {showTemplate && data?.template_version ? (
        <TemplateDrawer
          name={documentName}
          version={data.template_version}
          onClose={() => setShowTemplate(false)}
        />
      ) : null}

      {filling && (filling.fillable === false || filling.is_list) ? (
        <FillDialog
          chip={filling}
          busy={fill.isPending}
          error={fill.error}
          onCancel={() => {
            setFilling(null)
            fill.reset()
          }}
          onSubmit={(value) => fill.mutate({ slot: filling.slot, value })}
        />
      ) : null}
    </div>
  )
}

/**
 * The download, and the greyed version of it.
 *
 * A blocked action stays on screen with the rule that blocked it. A button that
 * vanished would teach the officer nothing about what would bring it back, and
 * the failing rule is exactly the instruction they need.
 */
function DownloadButton({
  caseId,
  versionId,
  blocking,
}: {
  caseId: string
  versionId: string
  blocking: { rule: string; text: string; citation?: string | null }[]
}) {
  const anchor = useRef<HTMLAnchorElement>(null)
  if (blocking.length) {
    const first = blocking[0]!
    return (
      <span className="blocked inline" data-testid="download-blocked">
        <button type="button" disabled>
          Download DOCX
        </button>
        {/* The rule in its own words, as the checks panel prints it — and the
            rule's id off the page there too (it stays on the element for
            whoever maintains the catalogue). */}
        <span className="why small" data-rule={first.rule || undefined}>
          {plainWords(first.text)}
          {first.citation ? <span className="faint"> {first.citation}</span> : null}
          {blocking.length > 1 ? (
            <span className="faint"> · and {blocking.length - 1} more</span>
          ) : null}
        </span>
      </span>
    )
  }
  return (
    <a
      ref={anchor}
      className="btn"
      href={api.docxPath(caseId, versionId)}
      download
      data-testid="download"
    >
      Download DOCX
    </a>
  )
}

/**
 * The variant selector, where a template's parts are **alternatives**.
 *
 * `PART_POLICY` records that D07's parts are the Rule 16(1)(a) memorandum *or*
 * the Rule 16(1)(b) one, never both, and that D10's are a notice, a notice
 * carrying the ex-parte warning, or a summons to a witness. Nothing read it, so
 * generation rendered every alternative into one document — a served instrument
 * that proceeds without an inquiry and also holds one (open item 44).
 *
 * **Only `choice` asks.** A bundle and a sequence issue together and are one
 * document; offering a selector there would ask the officer to decide something
 * the skeleton already decided, and choosing would drop half an instrument that
 * has to go out whole. Which mode this is comes off the server with the
 * document, like everything else on this screen.
 *
 * The reason travels with the choice, because a selector with no reason beside
 * it is a question the officer cannot answer.
 *
 * **A part is named in the pinned template's own words** — `part_labels`, the
 * heading the skeleton gives it — and never by a name written on this screen,
 * which would be a title hardcoded in a render path (stop list 6). A part the
 * template gives no heading keeps its key, `d07a`, rather than a guess.
 */
function partWords(part: string, labels?: Record<string, string> | null): string {
  return labels?.[part] ?? part
}

function VariantChoice({
  caseId,
  documentType,
  chosen,
  onChoose,
}: {
  caseId: string
  documentType: string
  chosen: string | null
  onChoose: (part: string | null) => void
}) {
  const preview = useQuery({
    queryKey: ['variants', caseId, documentType],
    queryFn: () => api.generateDocument(caseId, documentType, { preflight: true } as never),
    retry: false,
  })

  const data = preview.data
  if (!data || data.part_mode !== 'choice') return null
  const options = data.part_options ?? []

  return (
    <div className="row form" data-testid="part-choice" style={{ marginBottom: 10 }}>
      <label>
        Which instrument
        <select
          aria-label="Which instrument"
          value={chosen ?? data.part ?? options[0] ?? ''}
          onChange={(event) => onChoose(event.target.value)}
        >
          {options.map((option) => (
            <option key={option} value={option}>
              {partWords(option, data.part_labels)}
            </option>
          ))}
        </select>
      </label>
      {data.part_why ? <span className="small muted">{data.part_why}</span> : null}
    </div>
  )
}

/**
 * What a draft started on arrival was made as — and the way to choose again.
 *
 * Shown only on the version that start made, and only where there was a choice:
 * the purpose of a noting, or which of a template's alternative instruments.
 * *Choose again* returns to this document's own page, where the pickers and
 * *Generate* are; the draft being read is kept, as every version is.
 */
function DraftedAtOnce({
  purpose,
  part,
  partLabels,
  onChooseAgain,
}: {
  purpose: string | null
  part: string | null
  partLabels?: Record<string, string> | null
  onChooseAgain: () => void
}) {
  if (!purpose && !part) return null
  return (
    <p className="drafted-at-once small" role="status" data-testid="drafted-at-once">
      This draft was started for you
      {purpose ? (
        <>
          {' '}
          as <strong>{purpose}</strong>
        </>
      ) : null}
      {part ? (
        <>
          {purpose ? ', ' : ' '}
          instrument <strong>{partWords(part, partLabels)}</strong>
        </>
      ) : null}
      . Not what this one is for?{' '}
      <button type="button" className="link" onClick={onChooseAgain}>
        Choose again and draft another
      </button>{' '}
      — this draft is kept.
    </p>
  )
}

function LanguageChoice({
  caseId,
  documentType,
  chosen,
  onChoose,
}: {
  caseId: string
  documentType: string
  chosen: 'hi' | 'en' | null
  onChoose: (language: 'hi' | 'en' | null) => void
}) {
  // The same preflight the variant picker reads — one forecast, three controls.
  const preview = useQuery({
    queryKey: ['variants', caseId, documentType],
    queryFn: () => api.generateDocument(caseId, documentType, { preflight: true } as never),
    retry: false,
  })
  const available = preview.data?.languages_available ?? []
  // **A choice of one is not a choice.** Where the template can issue in one
  // language only, the pair of radios was a selected *Hindi* beside a greyed
  // *English — the office's English text for this document has not been
  // published*: a control that could not be operated, explaining at length why
  // not, on every document (walkthrough 32.34). Nothing is refused here that
  // the officer could have asked for, so there is nothing rule 3 needs said.
  // The language goes unasked and the server takes its own default — which is
  // the one there is, or this forecast would itself have been refused.
  if (available.length < 2) return null
  const canEnglish = available.includes('en')
  // What the server answers when nobody chooses is, by definition, this
  // factory's default — so the label is read off the same preflight rather
  // than assuming Hindi. In Tiruchirapalli it marks English.
  const factoryDefault = preview.data?.language === 'en' ? 'en' : 'hi'
  const value = chosen ?? factoryDefault

  return (
    <div
      className="row form choices"
      data-testid="language-choice"
      style={{ marginBottom: 10 }}
    >
      <label style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <input
          type="radio"
          name="doc-language"
          style={{ width: 'auto' }}
          checked={value === 'hi'}
          onChange={() => onChoose('hi')}
        />
        <span>
          Hindi{' '}
          {factoryDefault === 'hi' ? (
            <span className="small muted">— factory default</span>
          ) : null}
        </span>
      </label>
      <label
        style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}
        title={
          canEnglish
            ? 'Issued in English; the choice is pinned on the version. Provisional lines are flagged.'
            : "The office's English text for this document has not been published."
        }
      >
        <input
          type="radio"
          name="doc-language"
          style={{ width: 'auto' }}
          disabled={!canEnglish}
          checked={value === 'en'}
          onChange={() => onChoose('en')}
          data-testid="language-en"
        />
        {/* **Plainly "English".** The provisional note left the label on
            Archit's call (17 Sep 2026); the flag itself is unaffected — every
            provisional line still carries it on the rendered page, which is
            where an officer meets it. The unpublished case still says so,
            because a disabled control with no reason is rule 3's failure. */}
        <span className={canEnglish ? '' : 'muted'}>
          English{' '}
          {!canEnglish ? (
            <span className="small muted">
              — the office&apos;s English text for this document has not been published
            </span>
          ) : factoryDefault === 'en' ? (
            <span className="small muted">— factory default</span>
          ) : null}
        </span>
      </label>
    </div>
  )
}

/**
 * The purpose selector — what this noting is for.
 *
 * D01 is written at every stage and its closing line is **picked, not
 * drafted**: the officer's purpose selects the formula verbatim from the
 * skeleton's own table, and it tells the drafter what the note argues for. The
 * server suggests one from the case's record — no route yet suggests *start
 * proceedings*, a route with no chargesheet suggests *approve the draft
 * chargesheet* — and the officer confirms or changes it in one click.
 *
 * The suggestion is a suggestion, never a record: what was chosen rides the
 * version once generated. Rendered only when the pinned template declares
 * purposes, so every other document type sees nothing new.
 *
 * The query key is shared with `VariantChoice` deliberately — one preflight
 * serves both questions, not one call per component.
 */
function PurposeChoice({
  caseId,
  documentType,
  chosen,
  onChoose,
}: {
  caseId: string
  documentType: string
  chosen: string | null
  onChoose: (purpose: string | null) => void
}) {
  const preview = useQuery({
    queryKey: ['variants', caseId, documentType],
    queryFn: () => api.generateDocument(caseId, documentType, { preflight: true } as never),
    retry: false,
  })

  const data = preview.data
  const purposes = data?.purposes ?? []
  const suggested = data?.purpose_suggested ?? null

  /**
   * The suggestion pre-fills the control the moment it arrives, so the common
   * case is literally one click — Generate. It never overrides a hand-picked
   * value: the officer's choice is the one that rides the request.
   */
  useEffect(() => {
    if (!chosen && suggested && purposes.some((p) => p.key === suggested)) {
      onChoose(suggested)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when the suggestion lands
  }, [suggested])

  if (!purposes.length) return null

  return (
    <div className="row form" data-testid="purpose-choice" style={{ marginBottom: 10 }}>
      <label>
        Purpose of this noting
        <select
          aria-label="Purpose of this noting"
          value={chosen ?? suggested ?? ''}
          onChange={(event) => onChoose(event.target.value || null)}
        >
          <option value="">— not chosen: the closing line stays a blank —</option>
          {purposes.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label || option.key}
            </option>
          ))}
        </select>
      </label>
      {/* **The formula preview is gone, and the paragraph with it.** It showed
          the office's closing line straight off the table, which still carries
          its `{{co_name}}`, `{{d}}` and `{{rule}}` markers — so the officer was
          shown template source, unsubstituted, beside a picker. Nothing about
          the behaviour changes: the purpose still picks that formula verbatim
          and no model writes it. When this comes back it should show the line
          as it will render, not as it is stored. */}
      {chosen && chosen !== suggested ? null : suggested ? (
        <span className="small muted">
          Suggested from the case record — change it if this note is for something else.
        </span>
      ) : null}
    </div>
  )
}

/* ------------------------------------------- the drafting call, in two halves --
 *
 * The four-part call that drafted a version (docs/19 B0) says two different
 * kinds of thing, and they stood together in one block under the document:
 *
 * **What the officer has to know about the draft in front of them** — that the
 * drafting service did not answer, what the draft was written without, which
 * passages could not be put into English. That stays on the page, said plainly
 * (`DraftedWithout`).
 *
 * **How the draft was made** — which call, the version of its brief, rules and
 * example, the requests and tokens and what they cost, the pinned template and
 * exactly what the model was sent as the case. All of it is real and the owner
 * reads it; none of it is the document, and printed under every draft it was
 * the app's bookkeeping in the officer's way (walkthrough 32.34). It is one
 * closed disclosure at the foot of the page (`HowItWasMade`). Nothing is
 * removed.
 */
const callNumber = (value: unknown) => (typeof value === 'number' ? value : 0)

/** What a call's record lists under `key`, as words. */
function callList(call: Record<string, unknown>, key: string): string[] {
  const listed = call[key]
  return Array.isArray(listed) ? (listed as unknown[]).map(String) : []
}

function DraftedWithout({ call }: { call: Record<string, unknown> | null }) {
  if (!call || !call.call_key) return null
  const state = String(call.state ?? '')
  const missing = callList(call, 'missing')
  // What it was drafted without, as the drafting call said it under NOT ON THE
  // FILE (Phase A); a draft from before that carries the kinds alone.
  const needs = callList(call, 'needs')
  const english =
    call.english_rendering && typeof call.english_rendering === 'object'
      ? (call.english_rendering as Record<string, unknown>)
      : null
  const unrendered = english ? callNumber(english.unavailable) : 0
  if (state !== 'failed' && !needs.length && !missing.length && !unrendered) return null
  return (
    <div className="small" data-testid="drafted-without" style={{ margin: '6px 0' }}>
      {state === 'failed' ? (
        <div role="status">The drafting service did not answer: {String(call.error ?? '')}</div>
      ) : null}
      {needs.length ? (
        <div data-testid="call-missing">
          Drafted without what the file did not have — the draft marks where each was needed:
          <ul>
            {needs.map((need) => (
              <li key={need}>{need}</li>
            ))}
          </ul>
        </div>
      ) : missing.length ? (
        <div data-testid="call-missing">
          Drafted without: {missing.join(', ')} — the draft marks where each was needed.
        </div>
      ) : null}
      {unrendered ? (
        <div data-testid="call-unrendered">
          {unrendered} passage{unrendered === 1 ? '' : 's'} could not be put into English for
          this English draft, and went to the model as written.
        </div>
      ) : null}
    </div>
  )
}

function HowItWasMade({
  call,
  templateVersion,
  onOpenTemplate,
}: {
  call: Record<string, unknown> | null
  templateVersion: number | null
  onOpenTemplate: () => void
}) {
  const made = call && call.call_key ? call : null
  if (!made && templateVersion === null) return null
  const state = String(made?.state ?? '')
  const spec = String(made?.spec_version ?? '').slice(0, 8)
  const dropped =
    made && Array.isArray(made.dropped) ? (made.dropped as Record<string, unknown>[]) : []
  const casePart = made && typeof made.case_part === 'string' ? made.case_part : ''
  return (
    <details className="about" data-testid="how-it-was-made">
      <summary>
        How this draft was made
        {/* What the app left out is said on the closed line: closed is not
            hidden, and the count is of what is listed inside. */}
        {dropped.length ? (
          <span className="small muted" data-testid="call-dropped-count">
            {' '}
            · {dropped.length} thing{dropped.length === 1 ? '' : 's'} left out of the draft,
            each with its reason
          </span>
        ) : null}
      </summary>
      <div className="about-body small">
        {templateVersion !== null ? (
          <p className="muted">
            Template{' '}
            <button
              type="button"
              className="quiet"
              onClick={onOpenTemplate}
              data-testid="template-pin"
            >
              v{templateVersion} · pinned
            </button>
          </p>
        ) : null}
        {made ? (
          <div data-testid="call-stamp">
            <span className="muted">
              Drafted by one call · <span className="mono">{String(made.call_key)}</span>
              {spec ? (
                <>
                  {' '}
                  · brief, rules and example <span className="mono">{spec}</span>
                </>
              ) : null}
            </span>
            {state === 'pending' ? <span className="muted"> · drafting…</span> : null}
            {/* No cost line. The requests, tokens and rupees a draft took are not
                an officer's to act on, and are no longer shown to officers
                (18 Oct 2026). The call stamp keeps what the draft is: which
                call made it, and from which brief, rules and example. */}
          </div>
        ) : null}
        {dropped.length ? (
          <div data-testid="call-dropped">
            <div>
              {dropped.length} thing{dropped.length === 1 ? '' : 's'} the app left out of the
              draft, and why:
            </div>
            <ul>
              {dropped.map((item, index) => (
                <li key={index}>
                  {String(item.where ?? '')}: {String(item.reason ?? '')}
                  {item.source ? <span className="mono"> ({String(item.source)})</span> : null}
                  {item.clause ? (
                    <span className="mono"> (clause {String(item.clause)})</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {casePart ? (
          <div data-testid="call-case-part">
            <div>What the model was given about the case:</div>
            <pre className="mono small" style={{ whiteSpace: 'pre-wrap' }}>
              {casePart}
            </pre>
          </div>
        ) : null}
      </div>
    </details>
  )
}

/**
 * "Signed copy back? Attach it." Recorded at once, never refused; the version
 * it signs becomes the one later documents read. Issuing stays the officer's
 * own act, on the case page.
 */
function SignedCopyAttach({
  caseId,
  documentType,
  versionId,
  onRecorded,
}: {
  caseId: string
  documentType: string
  versionId: string
  onRecorded: () => void
}) {
  const [signedOn, setSignedOn] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const attach = useMutation({
    mutationFn: (file: File) =>
      api.recordSignedCopy(caseId, documentType, versionId, file, signedOn || undefined),
    onSuccess: (receipt) => {
      setDone(
        receipt.issued
          ? 'Signed copy recorded against this version.'
          : 'Signed copy recorded against this version. Mark the document issued on the case page when it goes out.',
      )
      onRecorded()
    },
  })
  return (
    <div className="row form small" data-testid="signed-copy" style={{ margin: '6px 0' }}>
      <label>
        Signed copy back? Attach it
        <input
          type="file"
          accept="application/pdf,image/*"
          aria-label="Attach the signed copy"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (file) attach.mutate(file)
          }}
        />
      </label>
      <label>
        Signed on
        <input
          type="date"
          value={signedOn}
          onChange={(event) => setSignedOn(event.target.value)}
        />
      </label>
      {attach.isPending ? <span className="muted">Recording…</span> : null}
      {attach.isError ? <ErrorEnvelope error={attach.error} /> : null}
      {done ? <span role="status">{done}</span> : null}
    </div>
  )
}
